import AsyncAutocomplete from '@components/v2/form/AsyncAutocomplete/AsyncAutocomplete';
import DatePicker from '@components/v2/form/DatePicker/DatePicker';
import Switch from '@components/v2/form/Switch/Switch';
import TextField from '@components/v2/form/TextField/TextField';
import OrderConditionCustomFieldType from '../../../../../../conditions/enums/orderConditionCustomFieldType.enum';
import OrderConditionCustomFieldConfig from '../../../../../../conditions/interfaces/orderConditionCustomFieldConfig.interface';

interface GenericValueOption {
  id: string;
  code: string;
  label: string;
}

interface OrderConditionCustomFieldControlProps {
  config: OrderConditionCustomFieldConfig;
  value: unknown;
  isDisabled: boolean;
  onChange: (value: unknown) => void;
}

const getCustomFieldDateValue = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return value;
  }

  if (!value) {
    return null;
  }

  const parsedDate = new Date(value as string);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const OrderConditionCustomFieldControl = ({
  config,
  value,
  isDisabled,
  onChange,
}: OrderConditionCustomFieldControlProps) => {
  switch (config.type) {
    case OrderConditionCustomFieldType.DATE: {
      const dateValue = getCustomFieldDateValue(value);

      return (
        <DatePicker
          size="small"
          disabled={isDisabled}
          label={config.label}
          value={dateValue}
          onChange={onChange}
        />
      );
    }

    case OrderConditionCustomFieldType.UNSIGNED_INTEGER:
      return (
        <TextField
          size="small"
          type="number"
          disabled={isDisabled}
          label={config.label}
          value={value == null ? '' : String(value)}
          onChange={(event) => onChange(event.target.value)}
          slotProps={{ htmlInput: { min: 0 } }}
        />
      );

    case OrderConditionCustomFieldType.BOOLEAN:
      return (
        <Switch
          label={config.label}
          checked={value === true || value === 'true'}
          disabled={isDisabled}
          onChange={(_, checked) => onChange(checked)}
        />
      );

    case OrderConditionCustomFieldType.GENERIC_VALUES:
      return (
        <AsyncAutocomplete<GenericValueOption>
          source={{
            url: `/orders/order-conditions/custom-fields/${config.id}/generic-values`,
            queryField: 'codeOrLabel',
            order: { code: 'ASC' },
          }}
          size="small"
          disabled={isDisabled}
          label={config.label}
          value={(value as string) ?? null}
          onChange={onChange}
          getOptionLabel={(option) => `${option.code} - ${option.label}`}
        />
      );

    default:
      return null;
  }
};

export default OrderConditionCustomFieldControl;
